import ClaimLoader from '../Components/ClaimLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useState, useEffect } from 'react';
import moment from 'moment';
import { claimCalendarPeriod, earliestDate, conditionStatuses, endDateStatuses } from '../claimRules';

import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';

const StepOne = ({ data, onUpdate, policyId, policy, errors, mutualGbs, claim = null, onLoadingChange }) => {
  const axios = useAxiosPrivate()

  const [staffList, setStaffList] = useState({});
  const [conditions, setConditions] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(data.selectedStaff || null)
  const [selectedCondition, setSelectedCondition] = useState(data.selectedCondition || '')
  const [absenceStart, setAbsenceStart] = useState(data?.absenceStart || '')
  const [absenceEnd, setAbsenceEnd] = useState(data?.absenceEnd || '')
  const [showPhased, setShowPhased] = useState(false)
  const [showConfidential, setShowConfidential] = useState(false)
  const [isPhasedReturn, setIsPhasedReturn] = useState(false)
  const [isShielding, setIsShielding] = useState(false)
  const [isConfidential, setIsConfidential] = useState(false)
  const [isLoadingStepOneData, setIsLoadingStepOneData] = useState(Boolean(policyId))

  useEffect(() => {
    onLoadingChange?.(isLoadingStepOneData);
  }, [isLoadingStepOneData, onLoadingChange]);

  useEffect(() => {
    onUpdate({
      selectedStaff,
      selectedCondition,
      absenceStart,
      absenceEnd,
      isPhasedReturn,
      isShielding,
      isConfidential
    });
  }, [
    selectedStaff,
    selectedCondition,
    absenceStart,
    absenceEnd,
    isPhasedReturn,
    isShielding,
    isConfidential
  ])

  useEffect(() => {
    if (!policyId) return
    setIsLoadingStepOneData(true)
    const fetchAll = async () => {
      try {
        await Promise.all([fetchStaffList(), fetchConditions()])
      } finally {
        setIsLoadingStepOneData(false)
      }
    }
    fetchAll()
  }, [policyId]);

  useEffect(() => {
    if (selectedCondition) {
      if (Number(selectedCondition.condition_phased) === 1) {
        setShowPhased(true);
      } else {
        setShowPhased(false);
        setIsPhasedReturn(false);
        setIsShielding(false);
        setShowConfidential(false);
        setIsConfidential(false);
      }

      const confidential = selectedCondition.condition_id === 'ed685ef6-7b54-11e7-a963-336a7d328401';
      setShowConfidential(confidential);
      if (!confidential) setIsConfidential(false);
    } else {
      setShowPhased(false);
      setIsPhasedReturn(false);
      setIsShielding(false);
      setShowConfidential(false);
      setIsConfidential(false);
    }
  }, [selectedCondition])

  const period = claimCalendarPeriod(policy, undefined, selectedStaff);
  const status = Number(claim?.claim_status_id ?? claim?.claimstatus ?? 0);
  const startMax = earliestDate(absenceEnd, period.end);

  useEffect(() => {
    if (!selectedStaff) return;
    const outside = value => value && ((period.start && value < period.start) || (period.end && value > period.end));
    if (outside(absenceStart)) {
      setAbsenceStart('');
      setAbsenceEnd('');
    } else if (outside(absenceEnd)) setAbsenceEnd('');
    // Reconcile dates when the policy or staff inception changes.
  }, [selectedStaff, period.start, period.end]);

  const fetchStaffList = async () => {
    try {
      const res = await axios.get(`/v1/staff-hub/policy_staffs/get-staff-list/${policyId}?api_token=${process.env.REACT_APP_KIS_TOKEN}`, {
        params: { claim_id: claim?.claim_id || claim?.id || 0 }
      });
      let response = res.data;
      // The API excludes ongoing active non-maternity claims for current policies
      // and returns archived staff directly for previous policies.
      setStaffList(Object.values(response).sort((a, b) => a.staff_name.localeCompare(b.staff_name)));
    } catch (err) {
      console.error('Error fetching staff list:', err);

    }
  };

  // pid delegates department/product/insurer exclusions and SAS WP overrides
  // to the API. Staff stress and maternity flags do not restrict conditions.
  const fetchConditions = async () => {
    try {
      const response = await axios.get(`api/v1/conditions?api_token=${process.env.REACT_APP_KIS_TOKEN}&ascending=1&pid=${policyId}`);
      const rawConditions = response.data.data;

      if (typeof rawConditions !== 'object') {
        console.error('Unexpected format for conditions');
        return;
      }

      const conditionArray = Object.values(rawConditions).filter(condition => !['training days', 'shared parental leave'].includes(condition.condition_title?.trim().toLowerCase())).sort((a, b) =>
        a.condition_title.localeCompare(b.condition_title)
      );
      setConditions(conditionArray);
    } catch (err) {
      console.error('Error fetching conditions:', err);
    }
  }

  const handleStaffChange = (e) => {
    setSelectedStaff(JSON.parse(e.target.value));
  }

  if (isLoadingStepOneData) return <ClaimLoader />;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {isLoadingStepOneData && (
        <div className="col-span-2 inline-flex items-center gap-2 text-sm text-sas">
          <span>Loading...</span>
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      )}
      <div>
        <label className="block mb-1 font-medium text-sm text-gray-700">Staff Name <span className='text-red-600'>*</span></label>
        <select
          required
          value={selectedStaff ? JSON.stringify(selectedStaff) : ''}
          onChange={handleStaffChange}
          disabled={isLoadingStepOneData || status >= 2}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        >
          <option value="" disabled>Select a staff member</option>
          {Object.values(staffList).map((staff, index) => {
            // Collect available details into an array to filter out missing ones
            const details = [];

            if (staff.fte) {
              details.push(`${staff.fte} FTE`);
            }
            if (staff.date_of_birth) {
              const dob = moment(staff.date_of_birth).format('DD/MM/YYYY');
              details.push(`DOB: ${dob}`);
            }
            if (staff.start_date) {
              const startDate = moment(staff.start_date).format('DD/MM/YYYY');
              details.push(`Incept: ${startDate}`);
            }

            // Format into a clean single line: "John Doe (1.0 FTE • DOB: 12/05/1990)"
            const label = details.length > 0
              ? `${staff.staff_name} (${details.join(' • ')})`
              : staff.staff_name;

            return (
              <option key={staff.id || index} value={JSON.stringify(staff)}>
                {label}
              </option>
            );
          })}
        </select>
      </div>
      <div>
        <label className="block mb-1 font-medium text-sm text-gray-700">Condition <span className='text-red-600'>*</span></label>
        <select
          required
          value={selectedCondition ? JSON.stringify(selectedCondition) : ''}
          onChange={(e) => setSelectedCondition(JSON.parse(e.target.value))}
          disabled={isLoadingStepOneData || !conditionStatuses.has(status)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        >
          <option value="" disabled>Select a condition</option>
          {conditions.map((cond) => (
            <option key={cond.condition_id} value={JSON.stringify(cond)} disabled={cond.disabled}>
              {cond.condition_title}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="absence-start" className="block mb-1 font-medium text-sm text-gray-700">First full day of absence <span className='text-red-600'>*</span></label>
        <input
          type="date"
          id="absence-start"
          value={absenceStart}
          min={period.start}
          max={startMax}
          onChange={(e) => setAbsenceStart(e.target.value)}
          disabled={isLoadingStepOneData || status >= 2}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label htmlFor="absence-end" className="block mb-1 font-medium text-sm text-gray-700">Last working day of absence (only enter once the staff member has returned, not their first day back)</label>
        <input
          type="date"
          id="absence-end"
          value={absenceEnd}
          min={[absenceStart, period.start].filter(Boolean).sort().pop() || ''}
          max={period.end}
          onChange={(e) => setAbsenceEnd(e.target.value)}
          disabled={isLoadingStepOneData || !absenceStart || !endDateStatuses.has(status)}
          // disabled={isEndDateDisabled}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>
      {selectedStaff?.staff_id && selectedCondition && !errors?.invalidCondition && (
        <>
          {!mutualGbs && (
            <fieldset style={{ marginBottom: 0 }}>
              <div className="form-group claim-config-form field-switch" style={{ color: 'red' }}>
                <label htmlFor="claim-shielding">
                  <input
                    type="checkbox"
                    className="checkbox mr-2"
                    id="claim-shielding"
                    checked={isShielding}
                    disabled={isLoadingStepOneData}
                    onChange={(e) => setIsShielding(e.target.checked)}
                  />
                  Is staff member shielding?
                </label>
              </div>
            </fieldset>
          )}

          {/* Confidential */}
          {showConfidential && (
            <fieldset style={{ marginBottom: 0 }}>
              <div className="form-group claim-config-form field-switch" style={{ color: 'grey' }}>
                <label htmlFor="claim-confidential">
                  <input
                    type="checkbox"
                    className="checkbox mr-2"
                    id="claim-confidential"
                    checked={isConfidential}
                    disabled={isLoadingStepOneData}
                    onChange={(e) => setIsConfidential(e.target.checked)}
                  />
                  Is this claim confidential? (this will hide the reason for absence for all users)
                </label>
              </div>
            </fieldset>
          )}

          {/* Phased return */}
          {showPhased && (
            <fieldset style={{ marginBottom: 0 }}>
              <div className="form-group claim-config-form phased-return-switch field-switch">
                <label htmlFor="claim-claim_phased">
                  <input
                    type="checkbox"
                    className="checkbox mr-2"
                    id="claim-claim_phased"
                    checked={isPhasedReturn}
                    disabled={isLoadingStepOneData}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsPhasedReturn(checked);
                      // if (checked) {
                      //   setAbsenceEnd('')
                      // }
                    }}
                  />
                  Is this a phased return?
                </label>
              </div>
            </fieldset>
          )}
        </>
      )}

      {(errors?.staff || errors?.condition) && (
        <p className="text-red-700 text-sm">{errors.staff || errors.condition}</p>
      )}
      {errors?.missingFields && (
        <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 text-sm px-4 py-3 mt-2 rounded">
          <strong>{errors.missingFields}</strong>
        </div>
      )}

      {errors?.incomplete && (
        <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 text-sm px-4 py-3 mt-2 rounded">
          <strong>{errors.incomplete}</strong>
        </div>
      )}

      {errors?.startDate && (
        <div className="bg-red-100 border border-red-300 text-red-800 text-sm px-4 py-3 mt-2 rounded">
          <strong>{errors.startDate}</strong>
        </div>
      )}

      {errors?.endDate && (
        <div role="alert" className="bg-red-100 border border-red-300 text-red-800 text-sm px-4 py-3 mt-2 rounded">
          <strong>{errors.endDate}</strong>
        </div>
      )}

      {errors?.invalidCondition && (
        <div className="bg-blue-100 border border-blue-300 text-blue-800 text-sm px-4 py-3 mt-2 rounded">
          <strong>{errors.invalidCondition}</strong><br />
          Please contact your Account Manager on 01773 814400 or speak to us using our Live Chat.
        </div>
      )}
    </div>
  )
};

export default StepOne;

